import { motion } from "framer-motion";
import { Shield, Lock, Server, Mic, Key, AlertTriangle } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { supportEmail } from "@/lib/companyBrand";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const TechCard = ({ icon: Icon, title, items }) => (
  <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
    <div className="flex items-center gap-2.5 mb-4">
      <Icon className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
      <h3 className="text-sm font-medium text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
        {title}
      </h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-500">
          <span className="w-1 h-1 rounded-full bg-zinc-700 shrink-0 mt-2" />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const SecurityPage = () => (
  <PublicLayout>
    <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-4xl mx-auto safe-area-padding">

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 md:mb-20"
      >
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Security</p>
        <h1
          className="text-3xl md:text-4xl font-light tracking-tight mb-5"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          How we protect your data
        </h1>
        <p className="text-sm text-zinc-400 leading-7 max-w-xl">
          Hans handles sensitive information — lesson recordings, student profiles, and
          teaching notes. Security isn't an afterthought here. Below is a transparent
          look at how we keep your data safe.
        </p>
      </motion.div>

      {/* Audio commitment — the most important thing */}
      <motion.div {...fadeInUp} className="mb-10">
        <div className="border border-[#f4cf08]/15 bg-[#f4cf08]/3 rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#f4cf08]/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
              <Mic className="w-5 h-5 text-[#f4cf08]" strokeWidth={1.5} />
            </div>
            <div>
              <h2
                className="text-base font-medium text-white mb-3"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Full recordings are deleted after processing
              </h2>
              <p className="text-sm text-zinc-400 leading-7">
                Audio is uploaded directly to AWS S3 via a short-lived presigned URL, processed
                by AssemblyAI (transcription) and OpenAI (analysis), and then deleted after processing succeeds or permanently fails.
                Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop.
                Consented practice clips are kept separately for up to 30 days.
              </p>
              <p className="text-sm text-zinc-400 leading-7 mt-3">
                Processing is automated. Written lesson notes and summaries remain available
                after full recordings are deleted; consented short practice clips expire separately.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Technical sections grid */}
      <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <TechCard
          icon={Lock}
          title="Encryption"
          items={[
            "All data in transit encrypted with TLS 1.2+",
            "Data at rest encrypted with AES-256 via AWS S3 server-side encryption",
            "On the web, session tokens are httponly, secure cookies that JavaScript cannot read",
            "The iPhone app keeps its session token in the iOS Keychain",
            "Presigned upload URLs expire within minutes",
          ]}
        />
        <TechCard
          icon={Key}
          title="Authentication"
          items={[
            "Sign in with Google or Apple (OAuth 2.0), or with an email address and password",
            "Passwords are stored only as salted bcrypt hashes, never as the password itself",
            "Session-based auth with 7-day expiry and automatic invalidation",
            "CORS policy restricts API access to authorized domains only",
          ]}
        />
        <TechCard
          icon={Server}
          title="Infrastructure"
          items={[
            "Hosted on AWS with enterprise-grade security controls",
            "Serverless compute via Lambda — no persistent servers to compromise",
            "Static assets via CloudFront CDN with HTTPS enforced",
            "Separate QA and production environments with isolated credentials",
          ]}
        />
        <TechCard
          icon={Shield}
          title="Access controls"
          items={[
            "Least-privilege IAM policies for all AWS services",
            "Secrets managed via AWS Secrets Manager",
            "API keys and credentials never stored in source code",
            "Instructors can only access their own student data",
          ]}
        />
      </motion.div>

      {/* Vulnerability reporting */}
      <motion.div {...fadeInUp}>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-5 h-5 text-zinc-500 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <h2
                className="text-sm font-medium text-white mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Found a vulnerability?
              </h2>
              <p className="text-sm text-zinc-500 leading-7">
                Please report it responsibly to{" "}
                <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">
                  {supportEmail()}
                </a>{" "}
                with a description and steps to reproduce. We'll acknowledge within 48 hours
                and we don't pursue legal action against good-faith security researchers.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

    </main>
  </PublicLayout>
);

export default SecurityPage;
